import * as React from 'react'
import type { FieldValueDto } from '@lib/dto/envelope.dto'
import type { SigningSessionDto } from '@lib/dto/signing-session.dto'
import type { CompleteSigningInput } from '@lib/input/signing.input'
import { isApiError } from '@/helpers/apiClient'
import { signService } from '@/services/SignService'
import { backfillSignedDates } from '@/signing/backfill-signed-dates'
import { DraftAutosave, type DraftSnapshot } from '@/signing/draft-autosave'
import { draftTexts, mergeDraft, type DraftTexts } from '@/signing/merge-draft'
import { seedDraftValues } from '@/signing/seed-draft-values'

/** The values this signer can save or submit, in the shape the API takes. */
function draftFields(
  session: SigningSessionDto,
  values: Record<string, FieldValueDto>,
): CompleteSigningInput['fields'] {
  return session.fields
    .filter((field) => field.signerId === session.signer.id)
    .filter((field) => !field.readOnly || field.type === 'date_signed')
    .flatMap((field) => {
      const value = values[field.id]
      return value === undefined ? [] : [{ id: field.id, value }]
    })
}

interface SigningDraft {
  values: Record<string, FieldValueDto>
  setValues: React.Dispatch<React.SetStateAction<Record<string, FieldValueDto>>>
  /** Whether a draft saved in another tab or window was just brought into this one. */
  mergedFromOtherTab: boolean
  /** Wait for the draft in flight, then save whatever is on screen. Later edits are not autosaved. */
  flushDraft: () => Promise<void>
  /** Field values to submit, read after `flushDraft` so a keystroke during that wait is included. */
  fieldsForComplete: () => CompleteSigningInput['fields']
}

interface DraftLatest {
  session: SigningSessionDto | undefined
  values: Record<string, FieldValueDto>
}

/** Thrown by a save that found a newer draft from another tab and merged it in; the merged draft saves next. */
class DraftMerged extends Error {
  constructor() {
    super('Merged a newer draft from another tab')
  }
}

function isDraftConflict<E>(error: E): boolean {
  return isApiError(error) && error.status === 409 && error.body.code === 'signing.draft_conflict'
}

/** How long the note about another tab's changes stays up. */
const MERGED_NOTE_MS = 10_000

/**
 * The signer's field values on screen, autosaved as a draft. Each save names
 * the draft revision it was based on; when another tab saved first, that
 * tab's draft is merged in (see `mergeDraft`) and saved again.
 */
export function useSigningDraft(
  token: string | undefined,
  session: SigningSessionDto | undefined,
  onError: (error: Error) => void,
  onSessionChanged: (session: SigningSessionDto) => void,
): SigningDraft {
  const [values, setValues] = React.useState<Record<string, FieldValueDto>>({})
  const latestRef = React.useRef<DraftLatest>({ session, values })
  const seededForRef = React.useRef<string | null>(null)
  // What this tab last saved (or loaded), and its revision: the base a save names, and a merge compares against.
  const baseRef = React.useRef<{ revision: number | undefined; texts: DraftTexts }>({ revision: undefined, texts: new Map() })
  const [mergedAt, setMergedAt] = React.useState<number | null>(null)
  const [autosave] = React.useState(
    () =>
      new DraftAutosave<CompleteSigningInput['fields']>(async () => {
        throw new Error('Missing signing token')
      }),
  )

  React.useEffect(() => {
    latestRef.current = { session, values }
  }, [session, values])

  React.useEffect(() => {
    /**
     * Another tab saved after this one's base: bring its draft in, keeping
     * the fields this tab changed, and let the merged draft save on the new
     * revision. If the signer finished or left in that tab, show that instead.
     */
    async function bringInLatest(signingToken: string): Promise<void> {
      const fresh = await signService.getSession(signingToken)
      if (fresh.gate !== 'ready' || !fresh.canSign) {
        onSessionChanged(fresh)
        return
      }
      const theirs = seedDraftValues(fresh.fields, fresh.signer.id)
      const merged = mergeDraft(baseRef.current.texts, latestRef.current.values, theirs)
      const saved = draftFields(fresh, theirs)
      baseRef.current = { revision: fresh.signer.draftRevision, texts: draftTexts(saved) }
      autosave.noteSaved(JSON.stringify(saved))
      latestRef.current = { session: latestRef.current.session, values: merged }
      setValues(merged)
      setMergedAt(Date.now())
    }

    autosave.setSave(
      async (payload) => {
        if (!token) {
          throw new Error('Missing signing token')
        }
        try {
          // No revision from an API that predates them: save unchecked, as it expects.
          const { revision } = baseRef.current
          const saved = await signService.saveDraft(token, revision === undefined ? { fields: payload } : { fields: payload, revision })
          baseRef.current = { revision: saved.signer.draftRevision, texts: draftTexts(payload) }
        } catch (error) {
          // While finishing, the values on screen are what gets signed: no merge then.
          if (!isDraftConflict(error) || autosave.isHeld) throw error
          await bringInLatest(token)
          throw new DraftMerged()
        }
      },
      (error) => {
        if (!(error instanceof DraftMerged)) onError(error)
      },
    )
  }, [autosave, onError, onSessionChanged, token])

  React.useEffect(() => {
    if (mergedAt === null) return
    const handle = window.setTimeout(() => setMergedAt(null), MERGED_NOTE_MS)
    return () => window.clearTimeout(handle)
  }, [mergedAt])

  const readDraft = React.useCallback((): DraftSnapshot<CompleteSigningInput['fields']> | null => {
    const currentSession = latestRef.current.session
    if (!currentSession || currentSession.gate !== 'ready' || !currentSession.canSign) {
      return null
    }
    const payload = draftFields(currentSession, latestRef.current.values)
    return { serialized: JSON.stringify(payload), payload }
  }, [])

  React.useEffect(() => {
    if (!session || session.gate !== 'ready') {
      return
    }
    const seedKey = `${session.envelope.id}:${session.signer.id}`
    if (seededForRef.current === seedKey) {
      return
    }
    const next = backfillSignedDates(session.fields, seedDraftValues(session.fields, session.signer.id), session.timeZone)
    // The sync effect above copied the previous state. This seed is what must
    // count as already saved, before that state re-renders.
    latestRef.current = { session, values: next }
    setValues(next)
    autosave.noteSaved(JSON.stringify(draftFields(session, next)))
    baseRef.current = { revision: session.signer.draftRevision, texts: draftTexts(draftFields(session, next)) }
    seededForRef.current = seedKey
  }, [autosave, session])

  React.useEffect(() => {
    if (!token || !session || session.gate !== 'ready' || !session.canSign || autosave.isHeld) {
      return
    }
    if (seededForRef.current !== `${session.envelope.id}:${session.signer.id}`) {
      return
    }
    const pending = readDraft()
    if (!pending || pending.serialized === autosave.savedSerialized()) {
      return
    }
    const handle = window.setTimeout(() => {
      autosave.enqueue(readDraft)
    }, 800)
    return () => window.clearTimeout(handle)
  }, [autosave, readDraft, session, token, values])

  const flushDraft = React.useCallback(async (): Promise<void> => {
    await autosave.flush(readDraft)
  }, [autosave, readDraft])

  const fieldsForComplete = React.useCallback((): CompleteSigningInput['fields'] => {
    const current = latestRef.current.session
    if (!current) {
      throw new Error('Missing signing session')
    }
    const withDates = backfillSignedDates(current.fields, latestRef.current.values, current.timeZone)
    return draftFields(current, withDates)
  }, [])

  return { values, setValues, mergedFromOtherTab: mergedAt !== null, flushDraft, fieldsForComplete }
}
